/**
 * PDP Shared Utilities
 *
 * Shared helpers used across PDP custom components:
 * - formatMoney: formats cents to a localized currency string
 * - addToCart: AJAX add-to-cart with button state management
 * - setupQtyStepper: wires up minus/input/plus quantity controls
 */

window.PdpUtils = {
  /**
   * Format price in cents to a currency string using the store's money format.
   * @param {number|null} cents
   * @returns {string}
   */
  formatMoney(cents) {
    if (cents == null) return '';
    const amount = (cents / 100).toFixed(2);
    // Use the theme's Shopify.formatMoney if available, otherwise fall back to $ prefix
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
    }
    return `$${amount}`;
  },

  /**
   * Add a variant to the cart via AJAX with button state management.
   * @param {number} variantId
   * @param {number} quantity
   * @param {HTMLButtonElement} btn - The button to update with loading/success/error states
   * @param {HTMLElement} element - The element to dispatch cart:update from
   * @returns {Promise<void>}
   */
  async addToCart(variantId, quantity, btn, element) {
    const originalText = btn.textContent;
    btn.textContent = 'Adding...';
    btn.disabled = true;

    const maxRetries = 3;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          btn.textContent = 'Retrying...';
          await new Promise((r) => setTimeout(r, 1000 * 2 ** (attempt - 1)));
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity }],
          }),
        });

        if (response.status === 429 && attempt < maxRetries) continue;

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          const message =
            response.status === 429
              ? 'Busy \u2014 try again'
              : errorData?.description || 'Could not add to cart';
          throw new Error(message);
        }

        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);

        element.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              source: 'product-form-component',
              itemCount: quantity,
            },
          },
        }));
        return;
      } catch (e) {
        if (attempt < maxRetries && e.message === 'Failed to fetch') continue;

        console.error('[PDP] Add to cart failed:', e);
        btn.textContent = originalText;
        btn.disabled = false;
        if (btn.dataset.puppet !== 'true') {
          this.showErrorTooltip(e.message, btn);
        }

        element.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { data: { source: 'product-form-component', message: e.message } },
        }));
        return;
      }
    }
  },

  /**
   * Show an error tooltip above a button element.
   * Uses position: fixed so it won't be clipped by overflow: hidden containers.
   * @param {string} message
   * @param {HTMLElement} anchorEl
   * @param {number} [duration=4000]
   */
  showErrorTooltip(message, anchorEl, duration = 4000) {
    // Remove any existing tooltip tied to this anchor
    const existingId = anchorEl.getAttribute('aria-describedby');
    if (existingId) {
      document.getElementById(existingId)?.remove();
    }

    const id = 'pdp-error-tooltip-' + Date.now();
    const tooltip = document.createElement('div');
    tooltip.className = 'pdp-tooltip pdp-error-tooltip';
    tooltip.id = id;
    tooltip.setAttribute('role', 'alert');
    tooltip.textContent = message;
    document.body.appendChild(tooltip);

    anchorEl.setAttribute('aria-describedby', id);

    const gap = 8;
    const margin = 12;

    function position() {
      const anchorRect = anchorEl.getBoundingClientRect();
      // If the anchor has scrolled out of view, dismiss
      if (anchorRect.bottom <= 0 || anchorRect.top > window.innerHeight) {
        dismiss();
        return;
      }
      const tooltipRect = tooltip.getBoundingClientRect();
      let left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
      left = Math.max(margin, Math.min(left, window.innerWidth - tooltipRect.width - margin));
      const arrowLeft = anchorRect.left + anchorRect.width / 2 - left;
      tooltip.style.left = left + 'px';
      tooltip.style.top = (anchorRect.top - tooltipRect.height - gap) + 'px';
      tooltip.style.setProperty('--arrow-left', arrowLeft + 'px');
    }

    function cleanup() {
      clearTimeout(dismissTimeout);
      tooltip.remove();
      if (anchorEl.getAttribute('aria-describedby') === id) {
        anchorEl.removeAttribute('aria-describedby');
      }
      window.removeEventListener('scroll', position, true);
      window.removeEventListener('resize', position);
    }

    function dismiss() {
      tooltip.classList.remove('pdp-error-tooltip--visible');
      tooltip.addEventListener('transitionend', cleanup, { once: true });
      setTimeout(cleanup, 500);
    }

    // Track anchor on scroll (capture phase to catch inner scrollable containers) and resize
    window.addEventListener('scroll', position, true);
    window.addEventListener('resize', position);

    position();
    requestAnimationFrame(() => tooltip.classList.add('pdp-error-tooltip--visible'));

    const dismissTimeout = setTimeout(dismiss, duration);
  },

  /**
   * Wire up a quantity stepper (minus button, input, plus button).
   * @param {HTMLButtonElement} minusBtn
   * @param {HTMLInputElement} input
   * @param {HTMLButtonElement} plusBtn
   */
  setupQtyStepper(minusBtn, input, plusBtn) {
    const min = parseInt(input.min, 10) || 1;

    function updateMinusState() {
      minusBtn.disabled = (parseInt(input.value, 10) || 1) <= min;
    }

    minusBtn.disabled = true;

    minusBtn.addEventListener('click', () => {
      const val = parseInt(input.value, 10) || 1;
      if (val > min) input.value = val - 1;
      updateMinusState();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    const max = parseInt(input.max, 10) || Infinity;

    plusBtn.addEventListener('click', () => {
      const val = parseInt(input.value, 10) || 1;
      if (val < max) input.value = val + 1;
      updateMinusState();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    input.addEventListener('change', () => {
      updateMinusState();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  },
};
